/**
 * Reperes poses dans le fil du texte : pastilles d'etat et touches de clavier.
 */
import type { ReactNode } from 'react';

export type TonBadge = 'neutre' | 'accent' | 'succes' | 'avertissement' | 'danger' | 'info';

const STYLES_BADGE: Record<TonBadge, string> = {
  neutre: 'border-bordure bg-surface-appuyee text-encre-douce',
  accent: 'border-accent-200 bg-accent-50 text-accent-800',
  succes: 'border-succes-200 bg-succes-50 text-succes-800',
  avertissement: 'border-avertissement-200 bg-avertissement-50 text-avertissement-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
  info: 'border-info-200 bg-info-50 text-info-800',
};

/**
 * Pastille d'etat.
 *
 * Le libelle est toujours rendu dans le DOM : la couleur ne fait que doubler
 * l'information, jamais la porter (exigence d'accessibilite).
 */
export function Badge({
  children,
  couleur,
  ton = 'neutre',
  className = '',
  titre,
}: {
  children: ReactNode;
  /** Couleur hexadecimale propre a l'entite (statut personnalisable). */
  couleur?: string;
  ton?: TonBadge;
  className?: string;
  titre?: string;
}) {
  if (couleur) {
    return (
      <span
        title={titre}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${className}`}
        style={{
          backgroundColor: `${couleur}14`,
          color: assombrir(couleur),
          borderColor: `${couleur}4d`,
        }}
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: couleur }}
        />
        {children}
      </span>
    );
  }
  return (
    <span
      title={titre}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STYLES_BADGE[ton]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Assombrit une couleur hexadecimale pour garantir un contraste suffisant
 * du texte sur un fond de la meme teinte tres clair.
 */
export function assombrir(hex: string, facteur = 0.55): string {
  const propre = hex.replace('#', '');
  if (propre.length !== 6) return 'var(--color-encre)';
  const composante = (debut: number): number => {
    const valeur = Number.parseInt(propre.slice(debut, debut + 2), 16);
    return Number.isFinite(valeur) ? Math.round(valeur * facteur) : 0;
  };
  return `rgb(${composante(0)}, ${composante(2)}, ${composante(4)})`;
}

/** Touche de clavier citee dans un texte d'aide (raccourcis). */
export function Kbd({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={`inline-flex h-5 min-w-5 items-center justify-center rounded-xs border border-bordure-forte bg-surface-douce px-1 font-sans text-2xs font-medium text-encre-discrete ${className}`}
    >
      {children}
    </kbd>
  );
}
